import { useState } from "react";

import { ApiError } from "../api/client";

/**
 * Form state plus the API's per-field errors. The API is the one validator (it has to be, a
 * phone system writes to it too); the form shows its words under the field they are about.
 */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const set =
    <K extends keyof T>(key: K) =>
    (value: T[K]) => {
      setValues((v) => ({ ...v, [key]: value }));
      setErrors((e) => {
        const { [key as string]: _drop, ...rest } = e;
        void _drop;
        return rest;
      });
    };

  /** Spread onto an input: value, onChange and class for a text field. */
  const text = <K extends keyof T>(key: K, extraClass = "") => ({
    value: (values[key] as string | null) ?? "",
    onChange: (e: { target: { value: string } }) => set(key)(e.target.value as T[K]),
    className: ["field", extraClass, errors[key as string] ? "is-invalid" : ""]
      .filter(Boolean)
      .join(" "),
    "aria-invalid": Boolean(errors[key as string]),
  });

  /** Record an API failure: per-field messages where it gave them, one line otherwise. */
  const fail = (err: unknown, fieldMap: Record<string, string> = {}) => {
    if (err instanceof ApiError && Object.keys(err.fields).length) {
      const mapped: Record<string, string> = {};
      for (const [path, message] of Object.entries(err.fields))
        mapped[fieldMap[path] ?? path] = message;
      setErrors(mapped);
      setFormError(null);
    } else {
      setFormError(err instanceof Error ? err.message : "Couldn't save. Try again.");
    }
  };

  return {
    values,
    set,
    text,
    errors,
    formError,
    setFormError,
    fail,
    reset: () => setValues(initial),
  };
}
